import { useTranslation } from "react-i18next";
import { contentFor, type Locale, type Product } from "@nh/shared";
import { DataTable, Td, Th, Tr } from "@/features/admin/components";
import { ProductPrices } from "./ProductPrices";
import { ProductRowActions } from "./ProductRowActions";
import { ProductStatusBadge } from "./ProductStatusBadge";
import { ProductStock } from "./ProductStock";
import { ProductThumb } from "./ProductThumb";

export interface ProductsTableProps {
  products: readonly Product[];
  onEdit: (product: Product) => void;
  onArchive: (product: Product) => void;
  /** Id del producto que se está archivando ahora mismo. */
  archivingId?: string | null;
}

/** El idioma del contenido que se enseña, derivado del de la interfaz. */
function contentLocale(language: string): Locale {
  return language.startsWith("en") ? "en" : "es";
}

export function ProductsTable({
  products,
  onEdit,
  onArchive,
  archivingId = null,
}: ProductsTableProps) {
  const { t, i18n } = useTranslation();
  const locale = contentLocale(i18n.language);

  return (
    <DataTable caption={t("Productos del catálogo")}>
      <thead>
        <tr>
          <Th>{t("Producto")}</Th>
          <Th>{t("Categoría")}</Th>
          <Th align="right">{t("Precio")}</Th>
          <Th align="right">{t("Existencias")}</Th>
          <Th align="center">{t("Estado")}</Th>
          <Th align="right" className="w-[110px]">
            <span className="sr-only">{t("Acciones")}</span>
          </Th>
        </tr>
      </thead>
      <tbody>
        {products.map((product) => {
          /*
           * `contentFor` cae al español cuando falta el inglés. Es
           * información incompleta a la vista, sí — y esa es la idea: un
           * hueco en blanco no le dice a nadie qué producto es esa fila.
           */
          const content = contentFor(product.translations, locale);
          return (
            <Tr key={product.id}>
              <Td>
                <div className="flex items-center gap-3">
                  <ProductThumb url={product.imageUrl} name={content.name} />
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ds-ink">{content.name}</p>
                    <p className="truncate font-mono text-[11px] text-ds-ink-3">
                      {product.slug}
                    </p>
                  </div>
                </div>
              </Td>
              <Td>
                {product.category ? (
                  <span className="text-[13px] text-ds-ink-3">{product.category}</span>
                ) : (
                  <span className="text-ds-ink-3">—</span>
                )}
              </Td>
              <Td align="right">
                <ProductPrices
                  prices={product.prices}
                  compareAtPrices={product.compareAtPrices}
                />
              </Td>
              <Td align="right">
                <ProductStock stock={product.stock} />
              </Td>
              <Td align="center">
                <ProductStatusBadge status={product.status} />
              </Td>
              <Td align="right">
                <ProductRowActions
                  product={product}
                  name={content.name}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  archiving={archivingId === product.id}
                />
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </DataTable>
  );
}
